import { DestroyRef, Directive, ElementRef, OnInit, inject, input } from '@angular/core';

/**
 * Impide escribir o pegar en un `p-inputNumber` entero un valor mayor que `appTopeValor`.
 *
 * Replica el KeyPressHandler del legacy (ej. `tbValorTransporteAdicional`): la tecla que deja
 * el valor por encima del tope se descarta y el campo conserva lo que tenía. No se usa `[max]`
 * de PrimeNG porque al salir del campo recorta el valor al máximo en lugar de rechazarlo.
 *
 * Solo para campos sin decimales (`[maxFractionDigits]="0"`): el valor se calcula con los
 * dígitos del texto. No modifica el modelo; la validación definitiva sigue en el formulario
 * y en el backend.
 *
 * Uso: `<p-inputNumber [appTopeValor]="max" [maxFractionDigits]="0" ... />`
 */
@Directive({
  selector: '[appTopeValor]',
  standalone: true,
})
export class TopeValorDirective implements OnInit {
  readonly appTopeValor = input.required<number>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // Captura: se ejecuta antes que los handlers del input interno de PrimeNG.
    this.host.addEventListener('keypress', this.onKeyPress, true);
    this.host.addEventListener('paste', this.onPaste, true);
    this.destroyRef.onDestroy(() => {
      this.host.removeEventListener('keypress', this.onKeyPress, true);
      this.host.removeEventListener('paste', this.onPaste, true);
    });
  }

  private readonly onKeyPress = (event: KeyboardEvent): void => {
    if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    this.bloquearSiExcede(event, event.key);
  };

  private readonly onPaste = (event: ClipboardEvent): void => {
    this.bloquearSiExcede(event, event.clipboardData?.getData('text') ?? '');
  };

  private bloquearSiExcede(event: Event, texto: string): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.readOnly || input.disabled) {
      return;
    }
    const inicio = input.selectionStart ?? input.value.length;
    const fin = input.selectionEnd ?? input.value.length;
    const resultante = input.value.slice(0, inicio) + texto + input.value.slice(fin);
    const digitos = resultante.replace(/\D/g, '');
    if (digitos && Number(digitos) > this.appTopeValor()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
